import { useEffect, useMemo, useState } from "react";
import { Search, MapPin, Phone, Globe, Building2, AlertCircle, ExternalLink } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export default function SuperintendentDirectoryPage() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/superintendents")
      .then((r) => setRows(r.data.counties || []))
      .catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(s) ||
        r.seat?.toLowerCase().includes(s) ||
        r.superintendent?.toLowerCase().includes(s),
    );
  }, [rows, q]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      <section className="bg-purple-dark text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3">
            Alabama county directory
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl tracking-tight mb-4">
            County Boards of Education
          </h1>
          <p className="text-white/85 leading-relaxed max-w-2xl">
            Every Alabama county Board of Education office. Use this directory
            to identify where to mail your BOE Withdrawal Notification Letter
            after enrolling at Zion Christian Academy.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <Input
              placeholder="Search by county, seat, or superintendent..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 border-grey"
              data-testid="superintendent-search"
            />
          </div>
          <div className="text-sm text-ink/60">
            {filtered ? `${filtered.length} of ${rows.length} counties` : "Loading…"}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-300 p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="text-amber-700 shrink-0 mt-0.5" size={16} />
          <div className="text-xs text-ink/80 leading-relaxed">
            Contact information is compiled from public directories. Superintendents
            change with elections and appointments. Before mailing your BOE letter,
            please verify the current superintendent&apos;s name at{" "}
            <a
              href="https://www.alabamaachieves.org"
              target="_blank"
              rel="noreferrer"
              className="text-purple hover:text-purple-dark underline"
            >
              alabamaachieves.org
            </a>{" "}
            or call the county BOE office directly.
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2" data-testid="superintendent-list">
          {filtered === null
            ? Array.from({ length: 8 }).map((_, i) => <CountySkeleton key={i} />)
            : filtered.map((c) => <CountyCard key={c.name} c={c} />)}
          {filtered !== null && filtered.length === 0 && (
            <div className="col-span-2 text-center text-ink/50 py-12 text-sm">
              No counties match your search.
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function CountySkeleton() {
  return (
    <div className="border border-grey bg-white p-5">
      <Skeleton className="h-6 w-40 mb-2" />
      <Skeleton className="h-3 w-32 mb-4" />
      <Skeleton className="h-3 w-full mb-2" />
      <Skeleton className="h-3 w-3/4 mb-2" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}

function CountyCard({ c }) {
  return (
    <div
      className="border border-grey bg-white p-5 hover:border-purple transition-colors"
      data-testid={`county-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="h-serif text-xl text-purple-dark tracking-tight">
            {c.name} County
          </h3>
          <div className="text-[10px] tracking-[0.2em] uppercase text-purple-light font-bold mt-0.5">
            <Building2 className="inline mr-1" size={11} /> BOE Office · {c.seat}
          </div>
        </div>
      </div>
      <div className="space-y-1.5 text-sm text-ink/85">
        {c.superintendent && (
          <div>
            <span className="text-[10px] tracking-widest uppercase font-bold text-purple-light">Superintendent · </span>
            <span>{c.superintendent}</span>
          </div>
        )}
        <div className="flex items-start gap-2">
          <MapPin size={13} className="text-ink/40 mt-1 shrink-0" />
          <span>{c.address}</span>
        </div>
        <div className="flex items-center gap-2">
          <Phone size={13} className="text-ink/40" />
          <a href={`tel:${c.phone.replace(/[^\d]/g, "")}`} className="hover:text-purple">
            {c.phone}
          </a>
        </div>
        {c.website && (
          <div className="flex items-center gap-2">
            <Globe size={13} className="text-ink/40" />
            <a
              href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
              target="_blank"
              rel="noreferrer"
              className="text-purple hover:text-purple-dark inline-flex items-center gap-1"
            >
              {c.website} <ExternalLink size={10} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
